import React, { useState } from "react";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { useAppDispatch } from "@core/store/hook.ts";
import { checkFileExists, importFileExternalAgent } from "@core/actions/dashboardExterneAction.ts";
import "./upload.css";
import { Dialog } from "primereact/dialog";

interface UploadProps {
  showUpload: boolean;
  handleShowUpload: () => void;
  setShowUpload: (a: boolean) => void;
  showMessage: (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: any
  ) => void;
  isImported: () => void;
}
const Upload: React.FC<UploadProps> = ({
  handleShowUpload,
  showMessage,
  isImported,
  setShowUpload,
}) => {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const fileUploadRef = React.useRef<any>(null);
  const [progress, setProgress] = useState(0);
  const [isError, setIsError] = useState(false);
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const [showMessageRequest, setShowMessageRequest] = useState('');
  const [dialogMessage, setDialogMessage] = useState<any>();
  const [visibleConfirmDialog, setVisibleConfirmDialog] =
    React.useState<boolean>(false);
  const [visibleConfirmDuplicates, setVisibleConfirmDuplicates] =
    React.useState<boolean>(false);

  const footerContent = (
    <div>
      <Button
        className="bg-orange-600 text-black border-none p-2"
        label="Modifier le fichier et recommencer l'importation"
        icon="pi pi-check"
        onClick={() => setShowUpload(false)}
        autoFocus
      />
    </div>
  );

    const updateClick = () => {
      setVisibleConfirmDuplicates(false);
      executeImport(file as File)
    }

    const cancelClick = () => {
      setVisibleConfirmDuplicates(false);
      setShowUpload(false);
    }

  const footerContentDuplicate = (
    <div className="flex justify-end gap-2">
      <div>
        <Button
          className="bg-red-500 text-black border-none p-2 m-1"
          label="Annuler"
          icon="pi pi-times"
          onClick={cancelClick}
          autoFocus
        />
        <Button
          className="bg-green-500 text-black border-none p-2 m-1"
          label="Remplacer les fichiers existants"
          icon="pi pi-check"
          onClick={updateClick}
          autoFocus
        />
      </div>
    </div>
  )
  const formatImportErrors = (
    errorData: Record<string, { column: string; message: string }[]>
  ) => {
    const formatColumnList = (columns: string[]) => {
      if (columns.length === 1) {
        return (
          <>
            la colonne <strong>{columns[0]}</strong>
          </>
        );
      }

      return (
        <>
          {columns.slice(0, -1).map((c, index) => (
            <span key={index}>
              la colonne <strong>{c}</strong>,{" "}
            </span>
          ))}
          et la colonne <strong>{columns[columns.length - 1]}</strong>
        </>
      );
    };

    const lines = Object.entries(errorData).map(
      ([line, errors], index, arr) => {
        const columnNames = errors.map((e) => e.column);
        return (
          <span key={line}>
            la ligne <strong>{line}</strong> dans{" "}
            {formatColumnList(columnNames)}
            {index < arr.length - 1 ? ", " : ""}
          </span>
        );
      }
    );
    const intro =
      lines.length > 1 ? "Il y a des erreurs sur " : "Il y a une erreur sur ";
    const indicationText =
      lines.length > 1
        ? "veuillez modifier ces lignes et refaire l'importation de votre fichier s'il vous plaît."
        : "veuillez modifier cette ligne et refaire l'importation de votre fichier s'il vous plaît.";

    return (
      <>
        {intro}
        {lines}, {indicationText}
      </>
    );
  };
  const successUpdate = (text: any) => {
    let textUpdate = "";
    let messages: string[] = [];
    for (const key of Object.keys(text)) {
      if (text[key] === "La ligne a été crée") {
        textUpdate = `La ligne ${key} a été créée`;
        messages.push(textUpdate);
      }
      if (text[key] === "La ligne a été modifiée") {
        textUpdate = `La ligne ${key} a été modifiée`;
        messages.push(textUpdate);
      }
    }
    return messages;
  };
  const errorIdentique = (text: any) => {
    let textUpdate = "";
    let messages: string[] = [];
    for (const key of Object.keys(text)) {
      if (text[key] === "La ligne est identique") {
        textUpdate = `Aucune modification sur la ligne ${key}`;
        messages.push(textUpdate);
      }
    }
    return messages;
  };

  // ---------------------------- Upload file ----------------------------
  const onUpload = (event: FileUploadHandlerEvent) => {
    const file = event.files[0];
    setFile(file)
    if (!file) return;
    setFileInfo({ name: file.name, size: file.size });
    handleImport(file);
  };


  const executeImport = (file:File) => {
    dispatch(
      importFileExternalAgent({
        file,
        onProgress: (percent) => {
          setProgress(percent);
          setShowMessageRequest(`Importation du fichier... `);
        },
        onSuccess: (data) => {
          if (Object.keys(data.states).length !== 0) {
            const valueIdentique = errorIdentique(data.states);
            const valueUpdate = successUpdate(data.states);
            if (valueIdentique.length !== 0) {
              valueIdentique.forEach((val: any, index: number) => {
                showMessage(
                  "warn",
                  "Attention",
                  <div key={index} className="font-semibold mt-2">
                    <div>{val}</div>
                    <div className="mt-2">Veuillez réessayer votre importation svp.</div>
                  </div>
                );
              });
            }
            if (valueUpdate.length !== 0) {
              valueUpdate.forEach((val: any, index: number)=>{
                showMessage(
                  "success",
                  "Succès",
                  <div key={index} className="font-semibold mt-2">
                      <div key={index}>{val}</div>
                  </div>
                );

              })
            }
            setShowUpload(false);
          } else {
            showMessage("success", "Succès", "Upload réussi avec succès !");
            setShowUpload(false);
          }
          setProgress(0);
          setFileInfo(null);
          handleShowUpload();
          isImported();
        },
        onError: (error) => {
          setProgress(0);
          setIsError(true);
          setFileInfo(null);
          if (error) {
            if (error.response.data.message === undefined) {
              const formattedMessage = formatImportErrors(
                error.response.data.errors
              );
              setDialogMessage(formattedMessage);
              setVisibleConfirmDialog(true);
            } else {
              setDialogMessage(
                "Merci de vérifier votre fichier avant de relancer l'importation."
              );
              setVisibleConfirmDialog(true);
            }
          }
        },
      })
    );
  }

  const handleImport = (file: File) => {
    dispatch(
      checkFileExists({
          file,
        onProgress: (percent) => {
            setProgress(percent);
            setShowMessageRequest(`Vérification du fichier... `);
          },
        onSuccess: (response) => {
          if (response.duplicate === true) {
            setVisibleConfirmDuplicates(true);
          } else {
            executeImport(file);
          }
        },
        onError: () => {
          setShowMessageRequest(`Erreur lors de la vérification du fichier.`);
          setIsError(true);
          showMessage(
            "error",
            "Erreur",
            "Impossible de vérifier le fichier."
          );
        },
      })
    );
  }

  // ---------------------------- Trigger import select --------------------
  const triggerFileSelect = () => {
    const inputElement = fileUploadRef.current?.getInput();
    if (inputElement) {
      inputElement.click();
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <>
      <div className="card flex justify-content-center">
        <Dialog
          header={
            <div
              style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}
            >
              <i
                className="pi pi-exclamation-triangle "
                style={{ fontSize: "25px", color: "orange" }}
              ></i>
              <span>Erreur détectée</span>
            </div>
          }
          visible={visibleConfirmDialog}
          style={{ width: "50vw" }}
          onHide={() => {
            if (!visibleConfirmDialog) return;
            setVisibleConfirmDialog(false);
            setShowUpload(false);
          }}
          footer={footerContent}
        >
          <p className="m-0">{dialogMessage}</p>
        </Dialog>
        {/* Dialog pour les doublons */}
        <Dialog 
          header={
            <div
              style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}
            >
              <i
                className="pi pi-exclamation-triangle "
                style={{ fontSize: "25px", color: "orange" }}
              ></i>
              <span>Doublons detecté </span>
            </div>
          }
          visible={visibleConfirmDuplicates}
          style={{ width: "50vw" }}
          onHide={() => {
            if (!visibleConfirmDuplicates) return;
            setVisibleConfirmDuplicates(false);
            setShowUpload(false);
          }}
          footer={footerContentDuplicate}
        >
          <p className="m-0">Fichier déjà existant. Voulez-vous l'écraser avec ce nouveau fichier?</p>
        </Dialog>
      </div>

      <div className="flex justify-center w-full">
        <Card className="p-4 w-[75%]">
          <div className="w-full flex justify-end">
            <i
              className="pi pi-times text-2xl cursor-pointer"
              onClick={handleShowUpload}
            ></i>
          </div>
          {fileInfo && (
            <div className="flex flex-col ">
              <div className="flex items-center mt-4 bg-gray-200 rounded-md p-2 relative overflow-hidden">
                {/* Barre de progression dynamique */}
                <div
                  className={`absolute top-0 left-0 h-full ${
                    isError 
                      ? "bg-gradient-to-r from-red-200 via-red-300 to-red-200" 
                      : "bg-gradient-to-r from-green-200 via-green-300 to-green-200"
                  }`}
                  style={{ width: `${progress}%` }}
                ></div>
                
                {/* Contenu de l'élément */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-8 h-8 mr-2 relative z-10"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <path d="M10 13l-2 2 2 2"></path>
                  <path d="M14 13l2 2-2 2"></path>
                </svg>
                <div className="relative z-10">
                  <p className="font-bold text-gray-800">{fileInfo.name}</p>
                  <p className="text-sm text-gray-600">
                    {formatFileSize(fileInfo.size)}
                  </p>
                </div>
              </div>
              <p className={`m-3 text-center  ${isError ? "text-red-500" : "animate-pulse"}`}>
                {showMessageRequest}
              </p>
            </div>
          )}

          <FileUpload
            ref={fileUploadRef}
            name="excel"
            accept=".xlsx"
            customUpload
            uploadHandler={onUpload}
            onSelect={() => {
              setTimeout(() => {
                fileUploadRef.current?.upload();
              }, 0);
            }}
            multiple={false}
            mode="advanced"
            chooseLabel="Uploader votre fichier"
            emptyTemplate={
              <div className="flex flex-col justify-center items-center">
                <Button onClick={triggerFileSelect} className="flex flex-col">
                  <i className="pi pi-file-plus text-green-900 text-5xl"></i>
                  <p className="uploader text-green-900 text-2xl">
                    Uploader votre fichier
                  </p>
                </Button>
                <p className="m-0">
                  ou glissez et déposez votre fichier Excel ici.
                </p>
              </div>
            }
          />
        </Card>
      </div>
    </>
  );
};

export default Upload;
